/*
	This class provides a common access point for operations relating to the state of the connection 
	between the acts in a venue's events and the artist profiles GUI. It can also be thought
	of as being primarily a wrapper around the 'artistConnection' field in the event collection. 

	Fields such as requestTime, artistId and requestEmail are currently being used to 
	indicate the state of this connection... ie they are pulling double duty. This is confusing, 
	but it has the advantage of being closer to providing a single source of truth wrt the state 
	of the connection.

	An alternative would be to add a field or fields to explicitly represent this state. 
	This might be clearer, but there would be more dependencies to maintain.

	Regardless of the approach taken, this class serves a purpose - in the first case it helps 
	divine the state of the connection, in the second it would help maintain consistency between 
	the fields.
*/

import { User } from 'Browser/User';
import { BrowserId } from 'Browser/BrowserId';
import { dbReplaceField } from 'Shared/forms/BaseOperations';
import { EventDoc } from 'Shared/model/Event';
import { LineupSet } from 'Shared/model/lineup';
import { IDbStore, IStoreWrap } from 'Shared/StoreProvider';
import { reconcile } from 'solid-js/store';
import Assert from 'Common/Assert';
import { ConnectionToServer } from 'Shared/Root';
import { subdoc } from 'Common/ViewUtils';
import { Id } from 'Common/Id';
//XXX CAREFUL with linking issues.
import type { ObjectId } from 'mongodb';


//XXX cf extending or copying for managing contract state

export class ArtistConnection
{
	connected(act:LineupSet)
	{
		return act.artistConnection?.artistId != null;
	}

	requestExists(act:LineupSet)
	{
		Assert.check(!this.connected(act));
		return act.artistConnection?.requestEmail != null;
	}

//TODO
	profileUpdateRequestExists(act:LineupSet)
	{
// INV true => connected
	}

//XXX BrowserIds here are part of a slow transition away from Id...

	async sendRequest(sw:IDbStore<EventDoc>,email:string,actKey:string)
	{
		const ret = await sw.server.requestArtistProfile.mutate({artistId:undefined,email,eventId:new BrowserId(sw.docId),actKey});		
		sw.setStore(...sw.location,'artistConnection',reconcile(ret));
	}

	disconnect(sw:IStoreWrap<EventDoc>)
	{
		dbReplaceField(sw,'artistConnection',null);
	}

//NOTE not being used yet. Disabled in ProfileStatus.tsx
	async requestProfileUpdate(sw:IDbStore<EventDoc>,data:LineupSet,actKey:string)
	{
		const ret = await sw.server.requestArtistUpdate.mutate({artistId:new BrowserId(data.artistConnection.artistId),email:undefined,eventId:new BrowserId(sw.docId),actKey});
		sw.setStore(...sw.location,'artistConnection',reconcile(ret));
	}

	async confirmProfile(server:ConnectionToServer,actKey:string,artistId:Id,eventId:Id)
	{
		await server.connectProfile.mutate({eventId:new BrowserId(eventId),actKey,artistId:new BrowserId(artistId)});
	}

	multiProfileRequestClause(user:User,permittedArtists:ObjectId[])
	{
		return [
			/* NOTE the indexes will apply to the terms in the $or clause */
			{$match:{$or:[
				{'lineup.artistConnection.requestEmail': user.email},
				{'lineup.artistConnection.artistId': {$in:permittedArtists}}
			]}},
			{$match:{'lineup.artistConnection.requestTime': {$exists:true}}},
		];
	}

	singleProfileRequestClause(user:User,artistId:ObjectId)
	{
		return [
			/* NOTE the indexes will apply to the terms in the $or clause */
			{$match:{$or:[
				{'lineup.artistConnection.requestEmail': user.email},
				{'lineup.artistConnection.artistId': artistId},
			]}},
			{$match:{'lineup.artistConnection.requestTime': {$exists:true}}},
		];
	}

	addConnectionToDocument(/*mod*/doc:EventDoc,actKey:string)
	{
		const lineup = subdoc(doc,['lineup']);
		const act = lineup.find(i => i.key == actKey);
		return act.artistConnection;
	}
//XXX Q: is it worth moving some of the related notification stuff here too? See rpc/connectProfile.ts
}

